// src/config/apiConfig.js

const protocol = process.env.REACT_APP_API_PROTOCOL || 'http';
const host     = process.env.REACT_APP_API_HOST || 'localhost';
const port     = process.env.REACT_APP_API_PORT || '';
const base     = process.env.REACT_APP_API_BASE || '/api';

const hostWithPort = port ? `${host}:${port}` : host;

// Which client surface this build is. FROZEN by the maintenance v3 contract:
// renaming a surface id is an outage-shaped change that raises no error on
// either side, so ids are added alongside, never renamed in place.
export const CLIENT_SURFACE = 'web';

// Printed in a support ticket's email (backend reply, 19 Sep 2026 §2.3). Set
// REACT_APP_CLIENT_VERSION at build time (a tag or commit hash); the fallback
// is the package version so the field is never blank.
export const CLIENT_VERSION = process.env.REACT_APP_CLIENT_VERSION || 'web-0.1.0';

// Every surface the backend knows about, in the order the admin panel shows
// them. `global` leads because it is the only one with reach beyond itself.
export const MAINTENANCE_SURFACES = [
  { id: 'global',  label: 'Everything',    hint: 'DB, droplet or API outage — gates every client' },
  { id: 'web',     label: 'Website',       hint: 'riftworks.tech' },
  { id: 'android', label: 'Android app',   hint: 'Kuestion for Android' },
  { id: 'ios',     label: 'iOS app',       hint: 'Kuestion for iOS' },
];

// Where a support ticket is emailed. Shown on the KB form; the address itself
// is set on the server (KB contract §3).
export const KUESTION_SUPPORT_EMAIL = 'support.kuestion@riftworks.tech';

export const API_BASE_URL = `${protocol}://${hostWithPort}${base}`;

export const API_ENDPOINTS = {
  // ── Preferences (cross-app: a person's, not Kuestion's). Contract v1.2. ──
  // GET / PATCH; user or student token; FLAT envelope ({status, timezone, …}).
  ME_PREFERENCES: `${API_BASE_URL}/me/preferences`,
  ORG_SETTINGS: `${API_BASE_URL}/org/settings`,           // GET / PATCH {default_timezone}; owner only; not built yet (404)

  // ── Knowledge Base feedback & support (contract 18 Sep 2026). No token
  // required — the KB is public — but one is sent when the tab holds one.
  // Every call carries X-KB-Session. Live since 19 Sep 2026.
  KB_FEEDBACK:     `${API_BASE_URL}/kb/feedback`,        // POST {article_id, helpful, app, locale} → 204
  KB_FEEDBACK_GAP: `${API_BASE_URL}/kb/feedback/gap`,    // POST {article_id, text, app} → 204
  KB_SUPPORT:      `${API_BASE_URL}/kb/support`,         // POST {subject, description, article_id, article_title, article_number, app, client_version} → 202 {reference, repliable}

  // ── Noticeboard (contract v1.1, live 3 Oct 2026). Advisory, never blocks.
  // Public read: no token, surface required, flat envelope {status, notices[]}.
  NOTICEBOARD:       (surface) => `${API_BASE_URL}/noticeboard?surface=${encodeURIComponent(surface)}`, // GET suite|kuestion
  ADMIN_NOTICEBOARD: `${API_BASE_URL}/admin/noticeboard`,                 // GET all / POST create (rwcore-admin)
  ADMIN_NOTICE:      (id) => `${API_BASE_URL}/admin/noticeboard/${encodeURIComponent(id)}`, // PATCH edit / DELETE withdraw

  // ── Auth (gwcoreusersdb) ──────────────────────────────────────────
  SIGNUP: `${API_BASE_URL}/signup`,
  LOGIN: `${API_BASE_URL}/login`,
  LOGOUT: `${API_BASE_URL}/logout`,
  VERIFY: `${API_BASE_URL}/verify`,
  RESEND_VERIFICATION: `${API_BASE_URL}/resend-verification`,
  FORGOT_PASSWORD: `${API_BASE_URL}/forgot-password`, // POST {identifier} — always generic success
  RESET_PASSWORD: `${API_BASE_URL}/reset-password`,   // POST {token, new_password}
  QUERY: `${API_BASE_URL}/query`,

  // ── WebAdmins — Maintenance & admin auth (webadminsdb) ────────────
  // Public read. `surface` is optional and defaults to `web` server-side, but
  // we always send it explicitly so the request says what it means.
  MAINTENANCE_STATUS: (surface = CLIENT_SURFACE) =>
    `${API_BASE_URL}/maintenance/status?surface=${encodeURIComponent(surface)}`,
  ADMIN_LOGIN:        `${API_BASE_URL}/admin/login`,        // POST (public)
  ADMIN_VERIFY:       `${API_BASE_URL}/admin/verify`,       // GET  (Bearer)
  ADMIN_LOGOUT:       `${API_BASE_URL}/admin/logout`,       // POST (Bearer)
  MAINTENANCE_ADMIN:  `${API_BASE_URL}/maintenance`,        // GET  (Bearer) every surface
  MAINTENANCE_SET:    `${API_BASE_URL}/maintenance`,        // PUT  (Bearer) {surface, maintenance, message}

  // ── BMan — Agents (bmandb) ────────────────────────────────────────
  AGENTS_AVAILABLE: `${API_BASE_URL}/agents/available`,
  AGENTS_KEY: `${API_BASE_URL}/agents/key`,
  AGENTS_KEY_REGENERATE: `${API_BASE_URL}/agents/key/regenerate`,
  AGENTS_REGISTERED: `${API_BASE_URL}/agents/registered`,
  AGENTS_REGISTER: `${API_BASE_URL}/agents/register`,

  // ── Kuestion — Manage Data (kuestiondb) ───────────────────────────
  KQ_FILE_UPLOAD:      `${API_BASE_URL}/kuestion/data/upload`,       // POST multipart
  KQ_FILE_LIST:        `${API_BASE_URL}/kuestion/data/files`,        // GET ?user_id=
  KQ_FILE_DELETE:      (id) => `${API_BASE_URL}/kuestion/data/files/${id}`,  // DELETE
  KQ_FILE_BULK_DELETE: `${API_BASE_URL}/kuestion/data/files/delete`, // POST {user_id, file_ids}

  // ── Kuestion — Question Banks ─────────────────────────────────────
  KQ_BANKS:            `${API_BASE_URL}/kuestion/banks`,             // GET
  KQ_BANK:             (id) => `${API_BASE_URL}/kuestion/banks/${id}`, // PUT {exam_code, module, question_type, reword_level} - PROPOSED, route not built yet

  // ── Kuestion — Exam Templates ─────────────────────────────────────
  KQ_TEMPLATES:        `${API_BASE_URL}/kuestion/templates`,         // GET / POST
  KQ_TEMPLATE:         (id) => `${API_BASE_URL}/kuestion/templates/${id}`, // GET / PUT / DELETE

  // ── Kuestion — Take Exam ──────────────────────────────────────────
  KQ_EXAM_START:       `${API_BASE_URL}/kuestion/exams/start`,       // POST {user_id, template_id}
  KQ_EXAM_SUBMIT:      (id) => `${API_BASE_URL}/kuestion/exams/${id}/submit`, // POST
  // Per-answer save (backend reply 2, 17 Sep). User token, same 3h grace as submit.
  KQ_EXAM_ANSWER:      (sessionId, questionId) => `${API_BASE_URL}/kuestion/exams/${sessionId}/answers/${questionId}`, // PUT {user_answer, is_flagged, seq}
  KQ_EXAM_HISTORY:     `${API_BASE_URL}/kuestion/exams/history`,     // GET ?user_id=

  // ── Kuestion — Solo topic strengths (contract v3 §7, §11) ─────────
  // Not in the v3 API table; assumed shape mirrors CAMPUS_ANALYTICS_ME.
  // See docs/Kuestion_Campus_v2_Frontend_Notes.md.
  KQ_ANALYTICS_ME:     `${API_BASE_URL}/kuestion/analytics/me`,      // GET (user token)

  // ── Kuestion Campus v3 (contract §9) ──────────────────────────────
  // Examiner endpoints take the existing user Bearer token. Student
  // endpoints take the separate student token issued by CAMPUS_STUDENT_LOGIN.
  CAMPUS_MODE:                 `${API_BASE_URL}/campus/mode`,                // GET / PUT {mode:"solo"|"campus"} — once
  CAMPUS_ORG:                  `${API_BASE_URL}/campus/org`,                 // GET / POST {display_name} / PUT {display_name}
  CAMPUS_STUDENTS:             `${API_BASE_URL}/campus/students`,            // GET / POST
  CAMPUS_STUDENTS_BULK:        `${API_BASE_URL}/campus/students/bulk`,       // POST multipart {file}
  CAMPUS_STUDENT:              (id) => `${API_BASE_URL}/campus/students/${id}`,           // PUT / DELETE
  CAMPUS_STUDENT_PASSWORD:     (id) => `${API_BASE_URL}/campus/students/${id}/password`,  // POST {password?}
  CAMPUS_STUDENT_LOGIN:        `${API_BASE_URL}/campus/student/login`,       // POST {login_id, password}
  CAMPUS_ASSIGNMENTS:          `${API_BASE_URL}/campus/assignments`,         // GET / POST
  CAMPUS_ASSIGNMENT:           (id) => `${API_BASE_URL}/campus/assignments/${id}`,        // DELETE
  CAMPUS_MY_ASSIGNMENTS:       `${API_BASE_URL}/campus/my/assignments`,      // GET (student) — status + in-progress session
  // An assigned sitting IS an exam session (backend response A2): start here,
  // then use KQ_EXAM_SUBMIT with the STUDENT token. There is no separate submit.
  CAMPUS_MY_ASSIGNMENT_START:  (id) => `${API_BASE_URL}/campus/my/assignments/${id}/start`,  // POST (student) → {session_id, questions, time_limit, seconds_remaining, mode}
  // Item 3 sitting routes (schema v2 §1, §5). Student token, 3h grace. Saves and
  // submits are judged against the stored expires_at with 30s of leeway.
  CAMPUS_MY_SITTING_SUBMIT:    (sittingId) => `${API_BASE_URL}/campus/my/sittings/${sittingId}/submit`, // POST {answers, time_taken}
  CAMPUS_MY_SITTING_ANSWER:    (sittingId, questionId) => `${API_BASE_URL}/campus/my/sittings/${sittingId}/answers/${questionId}`, // PUT {user_answer, is_flagged, seq}
  CAMPUS_ANALYTICS_ME:         `${API_BASE_URL}/campus/analytics/me`,        // GET (student) — modules[] + history[]
  CAMPUS_ANALYTICS_STUDENT:    (id) => `${API_BASE_URL}/campus/analytics/students/${id}`, // GET
  CAMPUS_ANALYTICS_COHORT:     `${API_BASE_URL}/campus/analytics/cohort`,    // GET — one call: modules[] + students[]
  // PDF reports are asynchronous jobs (backend response A5).
  CAMPUS_REPORT_STUDENT:       (id) => `${API_BASE_URL}/campus/reports/student/${id}`,         // POST → {job_id, status}
  CAMPUS_REPORT_MODULE:        (m) => `${API_BASE_URL}/campus/reports/module/${encodeURIComponent(m)}`, // POST → {job_id, status}
  CAMPUS_REPORT_JOB:           (jobId) => `${API_BASE_URL}/campus/reports/jobs/${jobId}`,     // GET → {status: queued|building|ready|failed, url?}
};

// A student login ID is always six alphanumerics, a hyphen, six alphanumerics.
// Usernames and emails can never take that shape, so the login form can route
// on it without asking the user which kind of account they hold (contract §9.3).
export const STUDENT_LOGIN_ID = /^[A-Za-z0-9]{6}-[A-Za-z0-9]{6}$/;
